/* eslint-disable max-lines */
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Fade, useTheme } from "@mui/material";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { hasInstantBookCriteriaUnmet } from "@src/appV2/OpenShifts/ShiftAction/hasInstantBookCriteriaUnmet";
import { SHIFT_NOTIFICATION_BLAST_DEEP_LINK_SOURCE } from "@src/appV2/redesign/Shift/constants";
import {
  BottomSheet,
  type UseModalState,
  useShake,
} from "@src/appV2/redesign/system/ui-components";
import { parseISO } from "date-fns";
import { useEffect, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { CSSTransition } from "react-transition-group";

import { calculateMinutesElapsed } from "../../ArrivalTime/calculateMinutesElapsed";
import { useIsArrivalTimeEligible } from "../../ArrivalTime/useIsArrivalTimeEligible";
import { useShiftModalsDataContext } from "../../ShiftDiscovery/useShiftModalsDataContext";
import { getShiftWorkDurationInHours } from "../../utils/getShiftWorkDuration";
import { useTrackOpportunityParameters } from "../../utils/useTrackOpportunityParameters";
import { useShiftDurationWithBreak } from "../hooks/useShiftDurationWithBreak";
import { convertShiftToSlotId } from "../Open/useGetShiftsSlotV2";
import { useRecordShiftBlastVisit } from "./api/useRecordShiftBlastVisit";
import { ShiftBottomSheetCardsWrapper } from "./CardsWrapper";
import { ShiftBottomSheetFooter } from "./Footer";
import { ShiftBottomSheetHeader } from "./Header";
import { ShiftBottomSheetContent } from "./SheetContent";
import { ShiftBottomSheetSkeleton } from "./SheetSkeleton";
import { ShiftFailedLoadingCard } from "./ShiftFailedLoadingCard";
import { ShiftNotBookableCard } from "./ShiftNotBookableCard";

export interface ShiftBottomSheetProps {
  modalState: UseModalState;
}

/**
 * A bottom sheet container for displaying shift details.
 *
 * The shift data can come from two sources:
 * 1. Directly via props when opened from a shift card
 * 2. Fetched from the API using the shift ID when:
 *    - Navigating between workplace views and browsing different shifts
 *    - Using back button in workplace modals going back to various shifts visited earlier
 *    - Opening a shift directly via URL
 */
export function ShiftBottomSheet(props: ShiftBottomSheetProps) {
  const { modalState } = props;
  const theme = useTheme();

  const {
    shift,
    shiftId,
    bookabilityStatus,
    isLoadingShiftModalsData,
    hasHoursRestrictionConflict,
    shiftFailedLoading,
    shiftFailedReasons,
    hasMarkedInterest,
    blockingRequirementsContext,
    shiftSlots,
    friendsMap,
    outboundFriendRequestsSet,
    worker,
    isIndependentContractorBadgesRequired,
    cancellationProtection,
    isCancellationProtectionPremiumApplied,
    isLoadingWorkplace,
    computedArrivalData,
    hasPreShiftOrientation,
    preShiftOrientationAdjustedStart: adjustedStart,
    preShiftOrientationLengthInMinutes: orientationLengthInMinutes,
    stateTransitionTimings,
  } = useShiftModalsDataContext();

  const { workplace } = shift ?? {};

  const effectiveStartIso =
    hasPreShiftOrientation && isDefined(adjustedStart)
      ? adjustedStart.toISOString()
      : shift?.attributes.start;

  const isLoading =
    (!isDefined(shift) || !isDefined(bookabilityStatus)) && isLoadingShiftModalsData;

  const loadingStartTime = useRef<number | undefined>(isLoading ? Date.now() : undefined);

  const isInstantBook = !hasInstantBookCriteriaUnmet(bookabilityStatus);
  const isShortLeadTime = useIsArrivalTimeEligible({
    shiftStartIso: effectiveStartIso,
    isInstantBook,
    workplaceType: workplace?.attributes?.type,
  });
  const targetArrivalTime = computedArrivalData?.targetArrivalTime;

  useLogEffect(
    APP_V2_USER_EVENTS.SHIFT_DETAILS_VIEWED,
    {
      shiftId,
      shift,
      isShortLeadTime,
      targetArrivalTime,
      bookabilityStatus,
      cancellationProtection,
      isCancellationProtectionPremiumApplied,
      loadingDuration: isDefined(loadingStartTime.current)
        ? Date.now() - loadingStartTime.current
        : undefined,
      total: stateTransitionTimings?.totalLoading.duration,
      transitions: stateTransitionTimings
        ? Object.fromEntries(
            Object.entries(stateTransitionTimings).map(([key, value]) => [key, value.duration])
          )
        : undefined,
    },
    { enabled: modalState.modalIsOpen && isDefined(shift) && !isLoading && !isLoadingWorkplace }
  );

  useLogEffect(
    APP_V2_USER_EVENTS.SHIFT_DETAILS_FAILED_TO_LOAD,
    { shiftId, bookabilityStatus, shiftFailedReasons },
    { enabled: modalState.modalIsOpen && shiftFailedLoading }
  );

  useTrackOpportunityParameters({
    workerId: worker.userId,
    opportunityId: shift?.id,
  });

  const location = useLocation();
  const history = useHistory();
  const isFromShiftBlast =
    new URLSearchParams(location.search).get("source") ===
    SHIFT_NOTIFICATION_BLAST_DEEP_LINK_SOURCE;

  useLogEffect(
    APP_V2_USER_EVENTS.SHIFT_BLAST_DEEP_LINK_VISITED,
    { shiftId },
    {
      enabled: isFromShiftBlast && isDefined(shiftId),
    }
  );

  const { mutate: recordBlastVisit } = useRecordShiftBlastVisit();

  useEffect(() => {
    if (!isFromShiftBlast || !isDefined(shiftId)) {
      return;
    }

    recordBlastVisit({ shiftId });

    const params = new URLSearchParams(location.search);
    params.delete("source");
    history.replace({
      pathname: location.pathname,
      search: params.toString(),
    });
  }, [isFromShiftBlast, shiftId, location.search, history, location.pathname, recordBlastVisit]);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { ref: bookabilityCardsShakeableRef, shake: shakeBookabilityCards } = useShake();

  const { totalDurationInHours, mandatoryBreakDurationInMinutes, requiresBreak } =
    useShiftDurationWithBreak({
      startDate: isDefined(effectiveStartIso) ? parseISO(effectiveStartIso) : new Date(),
      endDate: isDefined(shift) ? parseISO(shift.attributes.end) : new Date(),
      requiresLunchBreak: workplace?.attributes.requiresLunchBreak,
      breakPolicy: shift?.breakPolicy,
    });

  const startDate = isDefined(effectiveStartIso) ? parseISO(effectiveStartIso) : new Date();
  const minutesElapsed = calculateMinutesElapsed(startDate, new Date());

  const adjustedWorkDurationInHours =
    hasPreShiftOrientation && isDefined(effectiveStartIso) && isDefined(shift?.attributes.end)
      ? getShiftWorkDurationInHours(
          { start: effectiveStartIso, end: shift.attributes.end },
          requiresBreak,
          mandatoryBreakDurationInMinutes
        )
      : undefined;

  const showTransferableInfo =
    useCbhFlag(CbhFeatureFlag.SHIFT_TRANSFER_ENABLED, {
      defaultValue: false,
    }) &&
    shift?.attributes.isTransferrable &&
    !hasPreShiftOrientation;

  return (
    <BottomSheet
      modalState={modalState}
      footer={
        <ShiftBottomSheetFooter
          scrollContainerRef={scrollContainerRef}
          shakeBookabilityCards={shakeBookabilityCards}
          minutesElapsed={minutesElapsed}
          adjustedWorkDurationInHours={adjustedWorkDurationInHours}
          effectiveStartIso={effectiveStartIso}
        />
      }
      onClose={() => {
        logEvent(APP_V2_USER_EVENTS.SHIFT_DETAILS_CLOSED, { shiftId: shift?.id });
      }}
    >
      {isDefined(shift) && isDefined(workplace) && (
        <ShiftBottomSheetHeader
          shiftSlots={
            shiftSlots?.[
              convertShiftToSlotId({
                start: shift.attributes.start,
                end: shift.attributes.end,
                facilityId: workplace.id,
              })
            ]
          }
          isShortLeadTime={isShortLeadTime}
          onClose={() => {
            modalState.closeModal();
          }}
        />
      )}

      {isDefined(shift) && isDefined(workplace) && isDefined(bookabilityStatus) && !isLoading && (
        <ShiftBottomSheetContent
          shiftId={shift.id}
          shiftType={shift.attributes.type}
          shiftTimeSlot={shift.attributes.timeSlot}
          shiftStartIso={effectiveStartIso ?? shift.attributes.start}
          shiftEndIso={shift.attributes.end}
          shiftDurationInHours={totalDurationInHours}
          breakDurationInMinutes={mandatoryBreakDurationInMinutes}
          requiresBreak={requiresBreak}
          shiftQualification={shift.attributes.qualification}
          workplace={workplace}
          bookabilityStatus={bookabilityStatus}
          hasHoursRestrictionConflict={hasHoursRestrictionConflict}
          shiftFailedLoading={shiftFailedLoading}
          hasMarkedInterest={hasMarkedInterest}
          blockingRequirementsContext={blockingRequirementsContext}
          shiftSlots={
            shiftSlots?.[
              convertShiftToSlotId({
                start: shift.attributes.start,
                end: shift.attributes.end,
                facilityId: workplace.id,
              })
            ]
          }
          friendsMap={friendsMap}
          outboundFriendRequestsSet={outboundFriendRequestsSet}
          shiftSpecifications={shift.attributes.specifications}
          restrictedToWindow={shift.attributes.windowRestriction}
          scrollContainerRef={scrollContainerRef}
          bookabilityCardsShakeableRef={bookabilityCardsShakeableRef}
          isIndependentContractorBadgesRequired={isIndependentContractorBadgesRequired}
          cancellationProtection={cancellationProtection}
          isCancellationProtectionPremiumApplied={isCancellationProtectionPremiumApplied}
          priorityTill={
            isDefined(shift.attributes.priorityTill)
              ? new Date(shift.attributes.priorityTill)
              : undefined
          }
          showTransferableInfo={showTransferableInfo}
          window={shift.attributes.window}
          isShortLeadTime={isShortLeadTime}
          minutesElapsed={minutesElapsed}
          shiftAuction={shift.shiftAuction}
          computedArrivalData={computedArrivalData}
          preShiftOrientationAdjustedStart={adjustedStart}
          preShiftOrientationLengthInMinutes={orientationLengthInMinutes}
          isSingleOrientationShift={shift.attributes.orientationShiftType === "single-shift"}
        />
      )}
      {
        // This error state is displayed if the modal is opened
        // without initial data and the shift data is not available.
        !isDefined(shift) && shiftFailedLoading && (
          <ShiftBottomSheetCardsWrapper>
            {!isDefined(bookabilityStatus) && <ShiftFailedLoadingCard />}
            {isDefined(bookabilityStatus) && <ShiftNotBookableCard />}
          </ShiftBottomSheetCardsWrapper>
        )
      }

      <CSSTransition
        unmountOnExit
        in={isLoading && !shiftFailedLoading}
        timeout={theme.transitions.duration.leavingScreen}
      >
        <Fade in={isLoading && !shiftFailedLoading}>
          <Box>
            <ShiftBottomSheetSkeleton />
          </Box>
        </Fade>
      </CSSTransition>
    </BottomSheet>
  );
}
/* eslint-enable max-lines */
